import {
	shouldSkipSlide,
} from './slides';

/**
 * Calculate total slots and duration for a Visual Content Loop
 * This matches the logic used in the VisualContentLoop component
 *
 * @param {Array} providers - Array of provider objects with slides
 * @param {Object} dynamicDurations - Map of provider types to their durations
 * @returns {Object} - Object with totalSlots and totalDuration (in seconds)
 */
export const calculateVCLTotals = (providers, dynamicDurations = {}) => {
	if (!providers || !Array.isArray(providers)) {
		return {
			totalSlots:    0,
			totalDuration: 0,
		};
	}

	let totalSlots = 0;
	let totalDuration = 0;
	let totalBackfillNeeded = 0;

	// Separate providers into campaigns (non-backfill) and backfill
	const campaigns = providers.filter((p) => {
		return !p.is_backfill;
	});
	const backfillProviders = providers.filter((p) => {
		return p.is_backfill;
	});

	// 1. Calculate total slots (only from campaigns, not backfill)
	campaigns.forEach((provider) => {
		totalSlots += provider.slots || 0;
	});

	// 2. Calculate duration for campaigns and track backfill needed
	campaigns.forEach((provider) => {
		if (provider.type === 'slide_deck') {
			// For slide decks, sum up duration of active slides only
			let providerDuration = 0;
			let usedSlots = 0;

			if (provider.slides && Array.isArray(provider.slides)) {
				provider.slides.forEach((slide) => {
					// Check if slide should be skipped based on scheduling
					if (shouldSkipSlide(slide)) {
						return; // Skip this slide
					}

					// Count duration
					if (slide.item_type === 'folder') {
						// For folders, multiply duration by slots
						providerDuration += (slide.duration || 0) * (slide.folder_slots || 1);
						usedSlots += slide.folder_slots || 1;
					} else {
						providerDuration += slide.duration || 0;
						usedSlots += 1;
					}
				});
			}

			totalDuration += providerDuration;

			// Calculate backfill needed for this provider
			const backfillNeeded = Math.max(0, (provider.slots || 0) - usedSlots);
			totalBackfillNeeded += backfillNeeded;
		} else {
			// For dynamic providers (nova, monarch, etc.), use dynamic_durations
			const duration = dynamicDurations[provider.type] || 0;
			totalDuration += duration * (provider.slots || 0);
		}
	});

	// 3. Build a queue of durations from backfill providers
	const backfillDurations = [];
	backfillProviders.forEach((provider) => {
		if (provider.type === 'slide_deck') {
			if (provider.slides && Array.isArray(provider.slides)) {
				provider.slides.forEach((slide) => {
					// Check if slide should be skipped
					if (slide.scheduled && shouldSkipSlide(slide)) {
						return;
					}

					// For folders, add duration for each slot
					if (slide.item_type === 'folder') {
						for (let i = 0; i < (slide.folder_slots || 1); i++) {
							backfillDurations.push(slide.duration || 0);
						}
					} else {
						backfillDurations.push(slide.duration || 0);
					}
				});
			}
		} else {
			// Dynamic provider in backfill
			const duration = dynamicDurations[provider.type] || 0;
			for (let i = 0; i < (provider.slots || 0); i++) {
				backfillDurations.push(duration);
			}
		}
	});

	// 4. Add durations from backfill queue to fill the needed slots
	const backfillToAdd = backfillDurations.slice(0, totalBackfillNeeded);
	totalDuration += backfillToAdd.reduce((sum, duration) => {
		return sum + duration;
	}, 0);

	return {
		totalSlots,
		totalDuration, // in seconds
	};
};

/**
 * Format duration in seconds to MM:SS format
 * @param {number} totalSeconds - Duration in seconds
 * @returns {string} - Formatted duration string (e.g., "2:30")
 */
export const formatDuration = (totalSeconds) => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = String(totalSeconds % 60).padStart(2, '0');
	return `${minutes}:${seconds}`;
};
